import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Users, Briefcase, Contact2, LayoutDashboard, CalendarDays, MessageSquare, Settings, Wrench, BarChart3, DollarSign, Shield, FileSignature, Target, Building2, Database, Loader2, Bug, Filter } from 'lucide-react';
import Fuse from 'fuse.js';
import { searchService } from '../services/api';
import firmService from '../services/firmService';

const STATIC_PAGES = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard, keywords: ['home', 'overview', 'summary'] },
  { name: 'Calendar', path: '/calendar', icon: CalendarDays, keywords: ['schedule', 'events', 'appointments'] },
  { name: 'Cases', path: '/cases', icon: Briefcase, keywords: ['matters', 'litigation'] },
  { name: 'Clients', path: '/clients', icon: Users, keywords: ['contacts', 'customers'] },
  { name: 'Persons', path: '/persons', icon: Contact2, keywords: ['people', 'contacts', 'parties'] },
  { name: 'Communications', path: '/communications', icon: MessageSquare, keywords: ['messages', 'email', 'sms'] },
  { name: 'Invoices', path: '/invoices', icon: DollarSign, keywords: ['billing', 'payments', 'charges'] },
  { name: 'Trust Accounts', path: '/trust-accounts', icon: Shield, keywords: ['iolta', 'escrow', 'trust'] },
  { name: 'Operating Accounts', path: '/operating-accounts', icon: DollarSign, keywords: ['bank', 'operating', 'accounts'] },
  { name: 'Reports', path: '/reports', icon: BarChart3, keywords: ['analytics', 'statistics', 'data'] },
  { name: 'Templates', path: '/templates', icon: FileText, keywords: ['workflow', 'case templates'] },
  { name: 'Document Signing', path: '/signing', icon: FileSignature, keywords: ['esign', 'signature', 'envelopes'] },
  { name: 'Tools', path: '/tools', icon: Wrench, keywords: ['utilities', 'conflict check', 'redaction'] },
  { name: 'Goals', path: '/goals', icon: Target, keywords: ['targets', 'metrics', 'progress'] },
  { name: 'Users', path: '/users', icon: Users, keywords: ['team', 'staff', 'attorneys'] },
  { name: 'Settings', path: '/settings', icon: Settings, keywords: ['preferences', 'profile', 'configuration'] },
  { name: 'Calendar Settings', path: '/calendar/settings', icon: Settings, keywords: ['calendar config', 'google', 'outlook'] },
  { name: 'Firms', path: '/firms', icon: Building2, keywords: ['organizations', 'law firms'], superAdminOnly: true },
  { name: 'Migrations', path: '/migrations', icon: Database, keywords: ['database', 'migration', 'import'], superAdminOnly: true },
  { name: 'Dev Issues', path: '/admin/dev-issues', icon: Bug, keywords: ['kanban', 'bugs', 'tracker', 'issues', 'board', 'development'], superAdminOnly: true },
];

const CATEGORY_ICONS = {
  Pages: FileText,
  Clients: Users,
  Cases: Briefcase,
  Persons: Contact2,
  Events: CalendarDays,
  Notes: FileText,
  Documents: FileText,
};

const ENTITY_FILTERS = [
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'cases', label: 'Cases', icon: Briefcase },
  { key: 'persons', label: 'Persons', icon: Contact2 },
  { key: 'events', label: 'Events', icon: CalendarDays },
  { key: 'notes', label: 'Notes', icon: FileText },
  { key: 'documents', label: 'Documents', icon: FileText },
];

const ALL_ENTITY_KEYS = ENTITY_FILTERS.map(f => f.key);
const ENTITY_KEY_TO_TYPE = { clients: 'client', cases: 'case', persons: 'person', events: 'event', notes: 'note', documents: 'document' };

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [pageResults, setPageResults] = useState([]);
  const [entityResults, setEntityResults] = useState({ clients: [], cases: [], persons: [], events: [], notes: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [enabledFilters, setEnabledFilters] = useState(new Set(ALL_ENTITY_KEYS));
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const latestQueryRef = useRef('');
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Check super admin on mount
  useEffect(() => {
    if (isOpen) {
      firmService.checkSuperAdmin()
        .then(res => setIsSuperAdmin(res.is_super_admin))
        .catch(() => setIsSuperAdmin(false));
    }
  }, [isOpen]);

  // Filter static pages based on super admin status
  const availablePages = useMemo(() => {
    return STATIC_PAGES.filter(p => !p.superAdminOnly || isSuperAdmin);
  }, [isSuperAdmin]);

  // Fuse instance for fuzzy matching pages
  const fuse = useMemo(() => {
    return new Fuse(availablePages, {
      keys: [
        { name: 'name', weight: 2 },
        { name: 'keywords', weight: 1 },
      ],
      threshold: 0.4,
      includeScore: true,
    });
  }, [availablePages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setPageResults([]);
      setEntityResults({ clients: [], cases: [], persons: [], events: [], notes: [], documents: [] });
      setActiveIndex(0);
      setLoading(false);
      setEnabledFilters(new Set(ALL_ENTITY_KEYS));
      setShowFilters(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Build flattened results list for keyboard navigation, respecting filters
  const flatResults = useMemo(() => {
    const items = [];
    if (pageResults.length > 0) {
      pageResults.forEach(r => items.push({ type: 'page', data: r }));
    }
    for (const filter of ENTITY_FILTERS) {
      if (!enabledFilters.has(filter.key)) continue;
      const arr = entityResults[filter.key];
      if (arr?.length > 0) {
        arr.forEach(r => items.push({ type: ENTITY_KEY_TO_TYPE[filter.key], data: r }));
      }
    }
    return items;
  }, [pageResults, entityResults, enabledFilters]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [flatResults.length]);

  // Search handler
  const handleSearch = useCallback((value) => {
    setQuery(value);
    latestQueryRef.current = value;

    // Fuzzy search pages instantly
    if (value.length > 0) {
      const fuseResults = fuse.search(value).slice(0, 5);
      setPageResults(fuseResults.map(r => r.item));
    } else {
      setPageResults([]);
      setEntityResults({ clients: [], cases: [], persons: [], events: [], notes: [], documents: [] });
      setLoading(false);
      return;
    }

    // Debounced entity search
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length >= 2) {
      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await searchService.search(value, 25);
          // Stale-request protection
          if (latestQueryRef.current === value) {
            setEntityResults(res.data);
            setLoading(false);
          }
        } catch {
          if (latestQueryRef.current === value) {
            setLoading(false);
          }
        }
      }, 300);
    } else {
      setEntityResults({ clients: [], cases: [], persons: [], events: [], notes: [], documents: [] });
      setLoading(false);
    }
  }, [fuse]);

  // Navigate to selected result
  const handleSelect = useCallback((item) => {
    onClose();
    switch (item.type) {
      case 'page':
        navigate(item.data.path);
        break;
      case 'client':
        navigate(`/clients/${item.data.id}`);
        break;
      case 'case':
        navigate(`/cases/${item.data.id}`);
        break;
      case 'person':
        navigate(`/persons/${item.data.id}`);
        break;
      case 'event':
        navigate(`/calendar?date=${item.data.start_time ? new Date(item.data.start_time).toISOString().split('T')[0] : ''}`, {
          state: { highlightEvent: item.data.id }
        });
        break;
      case 'note':
        if (item.data.case_id) {
          navigate(`/cases/${item.data.case_id}?tab=notes`);
        } else if (item.data.client_id) {
          navigate(`/clients/${item.data.client_id}?tab=notes`);
        }
        break;
      case 'document':
        if (item.data.case_id) {
          navigate(`/cases/${item.data.case_id}?tab=documents`);
        }
        break;
      default:
        break;
    }
  }, [navigate, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (flatResults[activeIndex]) {
          handleSelect(flatResults[activeIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, flatResults, handleSelect, onClose]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const getDisplayName = (item) => {
    switch (item.type) {
      case 'page':
        return item.data.name;
      case 'client':
        return item.data.client_type === 'business'
          ? item.data.company_name || `${item.data.first_name} ${item.data.last_name}`
          : `${item.data.first_name} ${item.data.last_name}`;
      case 'case':
        return `${item.data.case_number ? item.data.case_number + ' - ' : ''}${item.data.title}`;
      case 'person':
        return item.data.company_name
          ? `${item.data.first_name} ${item.data.last_name} (${item.data.company_name})`
          : `${item.data.first_name} ${item.data.last_name}`;
      case 'event':
        return item.data.title || 'Untitled Event';
      case 'note':
        return item.data.title || 'Untitled Note';
      case 'document':
        return item.data.original_file_name || item.data.file_name || 'Untitled Document';
      default:
        return '';
    }
  };

  const getSubtext = (item) => {
    switch (item.type) {
      case 'page':
        return item.data.path;
      case 'client':
        return item.data.email || item.data.client_type;
      case 'case':
        return [item.data.case_type, item.data.status, item.data.client_name].filter(Boolean).join(' \u00b7 ');
      case 'person':
        return item.data.person_type || '';
      case 'event': {
        const parts = [item.data.event_type];
        if (item.data.start_time) {
          parts.push(new Date(item.data.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
        }
        if (item.data.location) parts.push(item.data.location);
        if (item.data.case_title) parts.push(item.data.case_title);
        return parts.filter(Boolean).join(' \u00b7 ');
      }
      case 'note':
        return [item.data.note_type, item.data.case_title || item.data.client_name].filter(Boolean).join(' \u00b7 ');
      case 'document':
        return [item.data.document_type, item.data.case_title].filter(Boolean).join(' \u00b7 ');
      default:
        return '';
    }
  };

  const getCategoryLabel = (type) => {
    const labels = { page: 'Pages', client: 'Clients', case: 'Cases', person: 'Persons', event: 'Events', note: 'Notes', document: 'Documents' };
    return labels[type] || type;
  };

  const getIcon = (item) => {
    if (item.type === 'page') {
      const Icon = item.data.icon;
      return <Icon className="h-4 w-4 text-gray-400" />;
    }
    const Icon = CATEGORY_ICONS[getCategoryLabel(item.type)];
    return Icon ? <Icon className="h-4 w-4 text-gray-400" /> : null;
  };

  // Group items by category for rendering headers
  let globalIndex = 0;
  const groups = [];
  let currentType = null;
  for (const item of flatResults) {
    if (item.type !== currentType) {
      currentType = item.type;
      groups.push({ type: 'header', label: getCategoryLabel(item.type) });
    }
    groups.push({ type: 'item', item, index: globalIndex });
    globalIndex++;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      {/* Palette */}
      <div className="fixed inset-x-0 top-0 z-50 flex justify-center pt-[15vh]">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
            <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search clients, cases, events, notes, documents..."
              className="flex-1 min-w-0 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none border-none shadow-none ring-0 focus:outline-none focus:border-none focus:shadow-none focus:ring-0"
              autoComplete="off"
              spellCheck={false}
            />
            {loading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />}
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`flex-shrink-0 p-1.5 rounded-md transition-colors ${
                showFilters ? 'bg-yellow-100 text-yellow-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              }`}
              title="Filter search categories"
            >
              <Filter className="h-4 w-4" />
            </button>
            <kbd className="hidden sm:inline-flex items-center flex-shrink-0 px-2 py-1 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-md">
              ESC
            </kbd>
          </div>

          {/* Entity Type Filters */}
          {showFilters && (
            <div className="px-4 py-2.5 border-b border-gray-100 flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  if (enabledFilters.size === ALL_ENTITY_KEYS.length) {
                    setEnabledFilters(new Set());
                  } else {
                    setEnabledFilters(new Set(ALL_ENTITY_KEYS));
                  }
                }}
                className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                  enabledFilters.size === ALL_ENTITY_KEYS.length
                    ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                    : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                All
              </button>
              {ENTITY_FILTERS.map(({ key, label, icon: Icon }) => {
                const active = enabledFilters.has(key);
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setEnabledFilters(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      });
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                      active
                        ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                        : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Results */}
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2">
            {groups.length === 0 && query.length > 0 && !loading && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No results found for "{query}"
              </div>
            )}
            {groups.length === 0 && query.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Start typing to search...
              </div>
            )}
            {groups.map((group, i) => {
              if (group.type === 'header') {
                return (
                  <div key={`header-${group.label}`} className="px-3 pt-3 pb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {group.label}
                    </span>
                  </div>
                );
              }
              const { item, index } = group;
              const isActive = index === activeIndex;
              return (
                <div
                  key={`${item.type}-${item.data.id || item.data.path}-${i}`}
                  data-index={index}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg transition-colors ${
                    isActive ? 'bg-yellow-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {getIcon(item)}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{getDisplayName(item)}</div>
                    {getSubtext(item) && (
                      <div className="text-xs text-gray-400 truncate">{getSubtext(item)}</div>
                    )}
                  </div>
                  {isActive && (
                    <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-xs text-gray-400 bg-gray-100 border border-gray-200 rounded-md">
                      Enter
                    </kbd>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded-md text-xs">&uarr;</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded-md text-xs">&darr;</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded-md text-xs">Enter</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded-md text-xs">Esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
